import type {
  FilterableSetting,
  SettingDrafts,
  SettingGroup,
  SettingsErrorMessageKey,
  SettingValue,
} from './types';

export function deriveSettingTranslationKey(
  key: string,
  field: 'label' | 'description',
): string {
  return `settings.${key.split(':').join('.')}.${field}`;
}

export function deriveSettingOptionTranslationKey(
  key: string,
  option: string,
): string {
  return `settings.${key.split(':').join('.')}.options.${option}`;
}

export function getSettingEnumOptions(options?: readonly unknown[]): string[] {
  return (options ?? []).filter(
    (option): option is string => typeof option === 'string',
  );
}

export function updateDrafts(
  drafts: SettingDrafts,
  key: string,
  value: SettingValue,
  authoritativeValue: SettingValue,
): SettingDrafts {
  if (Object.is(value, authoritativeValue)) {
    return Object.fromEntries(
      Object.entries(drafts).filter(([draftKey]) => draftKey !== key),
    );
  }

  return { ...drafts, [key]: value };
}

export function filterAndGroupSettings<T extends FilterableSetting>(
  settings: readonly T[],
  search: string,
  category: string,
): SettingGroup<T>[] {
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const filtered = settings.filter((setting) => {
    const matchesCategory = category === 'ALL' || setting.category === category;
    const matchesSearch =
      normalizedSearch.length === 0 ||
      `${setting.key} ${setting.category} ${setting.searchText}`
        .toLocaleLowerCase()
        .includes(normalizedSearch);
    return matchesCategory && matchesSearch;
  });

  const grouped = new Map<string, T[]>();
  [...filtered]
    .sort(
      (left, right) =>
        left.category.localeCompare(right.category) ||
        left.sortOrder - right.sortOrder ||
        left.key.localeCompare(right.key),
    )
    .forEach((setting) => {
      const categorySettings = grouped.get(setting.category) ?? [];
      categorySettings.push(setting);
      grouped.set(setting.category, categorySettings);
    });

  return [...grouped].map(([groupCategory, groupSettings]) => ({
    category: groupCategory,
    settings: groupSettings,
  }));
}

export function normalizeSettingDraftValue(
  valueType: string,
  input: unknown,
): SettingValue {
  if (valueType === 'BOOLEAN') {
    if (typeof input !== 'boolean') throw new Error('invalid-boolean');
    return input;
  }

  if (valueType === 'NUMBER' || valueType === 'DURATION_MS') {
    const value = typeof input === 'number' ? input : Number(input);
    if (!Number.isFinite(value)) throw new Error('invalid-number');
    return value;
  }

  if (typeof input !== 'string') throw new Error('invalid-string');
  if (valueType === 'CRON') return input;
  return input.trim();
}

export function normalizeSettingDraftValueSafely(
  valueType: string,
  input: unknown,
): SettingValue | undefined {
  try {
    return normalizeSettingDraftValue(valueType, input);
  } catch {
    return undefined;
  }
}

export function normalizeSettingSubmissionValue(
  valueType: string,
  value: SettingValue,
): SettingValue {
  if (
    (valueType === 'CRON' || valueType === 'TIMEZONE') &&
    typeof value === 'string'
  ) {
    return value.trim();
  }
  return value;
}

export function getCronFieldDefinitions(): ReadonlyArray<{
  key: 'minute' | 'hour' | 'dayOfMonth' | 'month' | 'dayOfWeek';
  min: number;
  max: number;
}> {
  return [
    { key: 'minute', min: 0, max: 59 },
    { key: 'hour', min: 0, max: 23 },
    { key: 'dayOfMonth', min: 1, max: 31 },
    { key: 'month', min: 1, max: 12 },
    { key: 'dayOfWeek', min: 0, max: 7 },
  ];
}

function isCronNumberInBounds(
  value: string,
  min: number,
  max: number,
): boolean {
  if (!/^\d+$/.test(value)) return false;
  const numericValue = Number(value);
  return numericValue >= min && numericValue <= max;
}

function isValidCronListItem(item: string, min: number, max: number): boolean {
  const stepParts = item.split('/');
  if (stepParts.length > 2 || stepParts.some((part) => part === '')) {
    return false;
  }

  const [base, step] = stepParts;
  if (step !== undefined && (!/^\d+$/.test(step) || Number(step) <= 0)) {
    return false;
  }
  if (base === '*') return true;

  const rangeParts = base.split('-');
  if (rangeParts.length === 1) {
    return isCronNumberInBounds(base, min, max);
  }
  if (rangeParts.length !== 2) return false;

  const [start, end] = rangeParts;
  return (
    isCronNumberInBounds(start, min, max) &&
    isCronNumberInBounds(end, min, max) &&
    Number(start) <= Number(end)
  );
}

const IZIVIA_CASES_CRON_SCHEDULE_KEY = 'izivia-monprojet-cases:cron:schedule';
const IZIVIA_WORKORDERS_CRON_SCHEDULE_KEY =
  'izivia-monprojet-workorders:cron:schedule';

export function resolveIziviaPeerCronScheduleKey(key: string): string | null {
  if (key === IZIVIA_CASES_CRON_SCHEDULE_KEY) {
    return IZIVIA_WORKORDERS_CRON_SCHEDULE_KEY;
  }
  if (key === IZIVIA_WORKORDERS_CRON_SCHEDULE_KEY) {
    return IZIVIA_CASES_CRON_SCHEDULE_KEY;
  }
  return null;
}

export function validateCronExpression(expression: string): boolean {
  const normalized = expression.trim();
  if (!normalized) return false;

  const fields = normalized.split(/\s+/);
  const definitions = getCronFieldDefinitions();
  if (fields.length !== definitions.length) return false;

  return fields.every((field, index) => {
    const definition = definitions.at(index);
    if (!definition || !field) return false;
    const items = field.split(',');
    return (
      items.length > 0 &&
      items.every(
        (item) =>
          item.length > 0 &&
          isValidCronListItem(item, definition.min, definition.max),
      )
    );
  });
}

export function normalizeReason(reason: string): string | undefined {
  const normalized = reason.trim();
  return normalized.length > 0 ? normalized : undefined;
}

export function getSettingConstraintEntries(
  constraints?: object,
): Array<{ key: string; value: string }> {
  if (!constraints) return [];

  return Object.entries(constraints)
    .filter((entry): entry is [string, string | number | boolean] =>
      ['string', 'number', 'boolean'].includes(typeof entry[1]),
    )
    .map(([key, value]) => ({ key, value: String(value) }));
}

export function getNumericSettingConstraints(constraints?: object): {
  min?: number;
  max?: number;
  step?: number;
} {
  const values = constraints as
    | { min?: unknown; max?: unknown; step?: unknown }
    | undefined;
  const finiteNumber = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined;

  return {
    min: finiteNumber(values?.min),
    max: finiteNumber(values?.max),
    step: finiteNumber(values?.step),
  };
}

export function resolveSettingsErrorMessageKey(
  status?: number,
): SettingsErrorMessageKey {
  switch (status) {
    case 400:
      return 'errors.validation';
    case 403:
      return 'errors.forbidden';
    case 404:
      return 'errors.notFound';
    case 409:
      return 'errors.conflict';
    default:
      return 'errors.generic';
  }
}
